import { UiEnhancedIWalletV2 } from 'src/utils/wallets/IWalletV2';
import { useWalletsV2 } from './useWalletsV2';
import { useCallback } from 'react';
import invariant from 'tiny-invariant';
import { FeePayerError } from 'src/constants/errors/FeePayerError';

/**
 * Hook to handle fee payer errors for authority-level (non-trading/non-delegatable) actions.
 * Uses authorityWallet with fee-payer, falls back to authorityWalletNoFeePayer on FeePayerError.
 */
export const useSponsoredSignDriftActionWithFallback = <
	T,
	Args extends unknown[],
>(
	action: (wallet: UiEnhancedIWalletV2, ...args: Args) => Promise<T>,
	disableGasSponsor = false
): ((...args: Args) => Promise<T>) => {
	const { authorityWallet, authorityWalletNoFeePayer } = useWalletsV2();

	const sponsoredSignDriftActionWithFallback = useCallback(
		async (...args: Args): Promise<T> => {
			if (disableGasSponsor) {
				invariant(
					authorityWalletNoFeePayer,
					'authorityWalletNoFeePayer is required'
				);
				return action(authorityWalletNoFeePayer, ...args);
			}

			invariant(authorityWallet, 'authorityWallet is required');

			try {
				return await action(authorityWallet, ...args);
			} catch (e) {
				if (e instanceof FeePayerError) {
					const fallbackWallet = authorityWalletNoFeePayer;

					invariant(fallbackWallet, 'fallbackWallet is required');

					console.log(
						'Failed to use fee payer, falling back to authority wallet signing'
					);

					return action(fallbackWallet, ...args);
				}
				throw e;
			}
		},
		[action, authorityWallet, authorityWalletNoFeePayer, disableGasSponsor]
	);

	return sponsoredSignDriftActionWithFallback;
};
